/**
 * ContactDrawer — uniforme right-hand slide-in voor sales-contacten.
 * Werkt voor 4 bronnen (kinds): signal_contact · primary · stakeholder · watchlist.
 *
 * Usage:
 *   <ContactDrawer contactRef={{ kind, signalId, leadId, contactId }} onClose={...} onChanged={...} />
 *
 * Fetches:  GET /contacts/context     (profiel + outreach + signalen + pipeline + watchlist-flag)
 * Actions:  POST /contacts/enrich     (Apollo → Surfe → LinkedIn-slug cascade)
 */
import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import i18n from "i18next";
import { LinkedInButton } from "@/components/common/LinkedInButton";
import CandidateAvatar from "@/components/CandidateAvatar";
import {
    X, Mail, Phone, Building2, Loader2, Sparkles, MessageCircle, Radar,
    ArrowRight, Eye, Send, Linkedin,
} from "lucide-react";

const TABS = [
    { id: "profile", labelKey: "cdrawer.profile", Icon: Sparkles },
    { id: "outreach", labelKey: "cdrawer.outreach", Icon: MessageCircle },
    { id: "signals", labelKey: "cdrawer.signals", Icon: Radar },
];

const SOURCE_BADGE = {
    apollo: "✓ Apollo", surfe: "✓ Surfe", theorg: "✓ The Org",
    linkedin: "✓ LinkedIn", perplexity: "✓ Perplexity", serper: "✓ Serper",
    apify: "✓ Apify", zevari: "✓ Zevari", manual: null,
};

function refParams(ref) {
    return {
        kind: ref.kind,
        ...(ref.signalId ? { signal_id: ref.signalId } : {}),
        ...(ref.leadId ? { lead_id: ref.leadId } : {}),
        ...(ref.contactId ? { contact_id: ref.contactId } : {}),
    };
}

export default function ContactDrawer({ contactRef, onClose, onChanged }) {
    const navigate = useNavigate();
    const [tab, setTab] = useState("profile");
    const [ctx, setCtx] = useState(null);
    const [loading, setLoading] = useState(true);
    const [enriching, setEnriching] = useState(false);
    const [showChannels, setShowChannels] = useState(false);
    const [drafting, setDrafting] = useState(null); // "linkedin" | "email" | null

    const load = () => {
        if (!contactRef) return;
        setLoading(true);
        api.get("/contacts/context", { params: refParams(contactRef) })
            .then((r) => setCtx(r.data))
            .catch((e) => { toast.error(e?.response?.data?.detail || i18n.t("common.failed")); onClose?.(); })
            .finally(() => setLoading(false));
    };
    useEffect(() => { setTab("profile"); load(); /* eslint-disable-next-line */ }, [JSON.stringify(contactRef)]);

    const enrich = async () => {
        setEnriching(true);
        try {
            const r = await api.post("/contacts/enrich", null, { params: refParams(contactRef) });
            if (r.data?.ok) {
                toast.success(i18n.t("sigcard.enrich_done", { source: r.data.source || "LinkedIn" }));
                load(); onChanged?.();
            } else {
                toast.info(r.data?.reason || i18n.t("sigcard.enrich_none"));
            }
        } catch (e) { toast.error(e?.response?.data?.detail || i18n.t("common.failed")); }
        finally { setEnriching(false); }
    };

    const startOutreach = async (channel) => {
        setDrafting(channel);
        try {
            const r = await api.post("/contacts/start-outreach", { channel }, { params: refParams(contactRef) });
            if (r.data?.ok) {
                toast.success(i18n.t("cdrawer.draft_created"), {
                    action: { label: i18n.t("cdrawer.view_outreach"), onClick: () => navigate("/app/sales/outreach") },
                });
                setShowChannels(false);
                load(); onChanged?.();
                setTab("outreach");
            }
        } catch (e) { toast.error(e?.response?.data?.detail || i18n.t("common.failed")); }
        finally { setDrafting(null); }
    };

    if (!contactRef) return null;
    const c = ctx?.contact;
    return (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm" onClick={onClose} data-testid="contact-drawer-backdrop">
            <div className="fixed right-0 top-0 bottom-0 w-full max-w-xl bg-[var(--jr-surface)] border-l border-white/10 shadow-2xl overflow-y-auto"
                onClick={(e) => e.stopPropagation()} data-testid="contact-drawer">
                {loading || !c ? (
                    <div className="p-10 text-center text-[var(--jr-text-2)]"><Loader2 className="animate-spin mx-auto" size={20} /></div>
                ) : (
                    <>
                        {/* Header */}
                        <div className="p-6 border-b border-white/8 sticky top-0 bg-[var(--jr-surface)]/95 backdrop-blur z-10">
                            <div className="flex items-start justify-between gap-3">
                                <div className="flex items-start gap-4 min-w-0 flex-1">
                                    <CandidateAvatar name={c.name} size={56} testid="cd-avatar" />
                                    <div className="min-w-0 flex-1">
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <h2 className="font-display text-xl tracking-tight text-white truncate" data-testid="cd-name">{c.name}</h2>
                                            {c.is_primary && <Badge cls="bg-[var(--jr-brand)]/15 text-[var(--jr-brand)]">Primary</Badge>}
                                            {c.network_degree && <Badge cls="bg-[#0a66c2]/15 text-[#0a66c2]">{c.network_degree} degree</Badge>}
                                            {SOURCE_BADGE[c.enrichment_source] && (
                                                <Badge cls="bg-[#22c55e]/15 text-[#22c55e]">{SOURCE_BADGE[c.enrichment_source]}</Badge>
                                            )}
                                            {ctx.on_watchlist && (
                                                <Badge cls="bg-[#38bdf8]/15 text-[#38bdf8]"><Eye size={9} className="inline mr-0.5" />{i18n.t("cdrawer.on_watchlist")}</Badge>
                                            )}
                                        </div>
                                        <div className="text-sm text-[var(--jr-text-2)] mt-0.5 truncate" data-testid="cd-role-company">
                                            {c.role || "—"}
                                            {ctx.company?.name && (
                                                <span className="text-[var(--jr-text-3)]"> · <Building2 size={11} className="inline mb-0.5" /> {ctx.company.name}</span>
                                            )}
                                        </div>
                                    </div>
                                </div>
                                <button onClick={onClose} data-testid="cd-close" className="p-1 text-[var(--jr-text-3)] hover:text-white"><X size={20} /></button>
                            </div>

                            {/* Contact strip */}
                            <div className="mt-4 flex items-center gap-2 flex-wrap">
                                <LinkedInButton url={c.linkedin_url} name={c.name} variant="pill" testid="cd-linkedin" />
                                {c.email && (
                                    <a href={`mailto:${c.email}`} data-testid="cd-email"
                                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-[var(--jr-info)]/40 bg-[var(--jr-info)]/10 text-[var(--jr-info)] hover:bg-[var(--jr-info)]/20 text-xs">
                                        <Mail size={12} /> {c.email}
                                    </a>
                                )}
                                {c.phone && (
                                    <a href={`tel:${c.phone.replace(/\s/g, "")}`} data-testid="cd-phone"
                                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-[#22c55e]/40 bg-[#22c55e]/10 text-[#22c55e] hover:bg-[#22c55e]/20 text-xs">
                                        <Phone size={12} /> {c.phone}
                                    </a>
                                )}
                                {contactRef.kind !== "watchlist" && (
                                    <button onClick={enrich} disabled={enriching} data-testid="cd-enrich-btn"
                                        title={i18n.t("sigcard.enrich_tip")}
                                        className="ml-auto inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[var(--jr-brand)] text-black text-xs font-medium hover:bg-[var(--jr-brand-2)] disabled:opacity-50">
                                        {enriching ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />}
                                        {i18n.t("sigcard.enrich_btn")}
                                    </button>
                                )}
                                <button onClick={() => setShowChannels(!showChannels)} data-testid="cd-start-outreach"
                                    disabled={!c.linkedin_url && !c.email}
                                    title={(!c.linkedin_url && !c.email) ? i18n.t("cdrawer.no_channel") : i18n.t("cdrawer.start_outreach_tip")}
                                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-xs font-medium disabled:opacity-40 transition-colors ${showChannels
                                        ? "border-[#22c55e] bg-[#22c55e]/20 text-[#22c55e]"
                                        : "border-[#22c55e]/40 bg-[#22c55e]/10 text-[#22c55e] hover:bg-[#22c55e]/20"} ${contactRef.kind === "watchlist" ? "ml-auto" : ""}`}>
                                    <Send size={12} /> {i18n.t("cdrawer.start_outreach")}
                                </button>
                            </div>

                            {/* Kanaalkeuze voor 1-klik outreach */}
                            {showChannels && (
                                <div className="mt-2 flex items-center gap-2 flex-wrap" data-testid="cd-channel-picker">
                                    <span className="text-[10px] font-mono uppercase tracking-[0.16em] text-[var(--jr-text-3)]">{i18n.t("cdrawer.pick_channel")}</span>
                                    <button onClick={() => startOutreach("linkedin")} disabled={!c.linkedin_url || !!drafting}
                                        data-testid="cd-channel-linkedin"
                                        title={!c.linkedin_url ? i18n.t("cdrawer.no_linkedin") : ""}
                                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-[#0a66c2]/50 bg-[#0a66c2]/10 text-[#4d9fe8] text-xs hover:bg-[#0a66c2]/25 disabled:opacity-40">
                                        {drafting === "linkedin" ? <Loader2 size={12} className="animate-spin" /> : <Linkedin size={12} />} LinkedIn DM
                                    </button>
                                    <button onClick={() => startOutreach("email")} disabled={!c.email || !!drafting}
                                        data-testid="cd-channel-email"
                                        title={!c.email ? i18n.t("cdrawer.no_email") : ""}
                                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-[var(--jr-info)]/50 bg-[var(--jr-info)]/10 text-[var(--jr-info)] text-xs hover:bg-[var(--jr-info)]/25 disabled:opacity-40">
                                        {drafting === "email" ? <Loader2 size={12} className="animate-spin" /> : <Mail size={12} />} E-mail
                                    </button>
                                </div>
                            )}

                            {/* Tabs */}
                            <div className="mt-5 flex items-center gap-1 flex-wrap border-b border-white/8 -mb-6 pb-0">
                                {TABS.map((tm) => {
                                    const active = tab === tm.id;
                                    const count = tm.id === "outreach" ? (ctx.outreach || []).length
                                        : tm.id === "signals" ? (ctx.signals || []).length : null;
                                    return (
                                        <button key={tm.id} onClick={() => setTab(tm.id)} data-testid={`cd-tab-${tm.id}`}
                                            className={`text-xs px-3 py-2 border-b-2 transition-colors inline-flex items-center gap-1.5 ${active
                                                ? "border-[var(--jr-brand)] text-white"
                                                : "border-transparent text-[var(--jr-text-3)] hover:text-white"}`}>
                                            <tm.Icon size={12} /> {i18n.t(tm.labelKey)}
                                            {count != null && count > 0 && (
                                                <span className={`text-[9px] font-mono px-1 rounded ${active ? "bg-[var(--jr-brand)] text-black" : "bg-white/8 text-[var(--jr-text-3)]"}`}>{count}</span>
                                            )}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>

                        {/* Body */}
                        <div className="p-6">
                            {tab === "profile" && <ProfileTab c={c} ctx={ctx} />}
                            {tab === "outreach" && <OutreachList items={ctx.outreach || []} />}
                            {tab === "signals" && <SignalsList items={ctx.signals || []} />}
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}

function ProfileTab({ c, ctx }) {
    return (
        <div className="space-y-5">
            <div className="grid sm:grid-cols-2 gap-4">
                <Kv label="Email" value={c.email || "—"} />
                <Kv label={i18n.t("cdrawer.phone")} value={c.phone || "—"} />
                <Kv label={i18n.t("cdrawer.degree")} value={c.network_degree ? `${c.network_degree} degree` : "—"} />
                <Kv label={i18n.t("cdrawer.source")} value={c.enrichment_source || "—"} />
                {c.influence && <Kv label={i18n.t("cdrawer.influence")} value={c.influence} />}
                {c.sentiment && <Kv label={i18n.t("cdrawer.sentiment")} value={c.sentiment} />}
                <Kv label={i18n.t("cdrawer.company")} value={ctx.company?.name || "—"} />
                <Kv label={i18n.t("cdrawer.stage")} value={ctx.lead?.stage || "—"} />
            </div>
            {c.notes && (
                <div>
                    <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-[var(--jr-text-3)] mb-1">{i18n.t("cdrawer.notes")}</div>
                    <p className="text-sm text-[var(--jr-text-2)] leading-relaxed">{c.notes}</p>
                </div>
            )}
            {ctx.lead?.id && (
                <Link to="/app/sales/pipeline" data-testid="cd-view-pipeline"
                    className="inline-flex items-center gap-1.5 text-xs text-[var(--jr-info)] hover:text-white">
                    {i18n.t("cdrawer.view_pipeline")} <ArrowRight size={11} />
                </Link>
            )}
        </div>
    );
}

function OutreachList({ items }) {
    if (items.length === 0) return <Empty>{i18n.t("cdrawer.no_outreach")}</Empty>;
    return (
        <div className="space-y-2">
            {items.map((o) => (
                <div key={o.id} className="rounded-lg border border-white/10 bg-[var(--jr-surface-2)] p-3" data-testid={`cd-msg-${o.id}`}>
                    <div className="flex items-center gap-2 flex-wrap text-[10px] font-mono uppercase tracking-[0.16em] text-[var(--jr-text-3)]">
                        <span>{o.channel}</span>
                        {o.status && <span className="px-1 rounded bg-white/6 normal-case">{o.status}</span>}
                        <span>· {(o.sent_at || o.created_at || "").slice(0, 10)}</span>
                        {o.on_behalf_of_user_name && <span className="normal-case">namens {o.on_behalf_of_user_name}</span>}
                    </div>
                    {o.subject && <div className="text-xs font-medium text-white mt-1">{o.subject}</div>}
                    <p className="text-xs text-[var(--jr-text-2)] mt-1 line-clamp-3 whitespace-pre-wrap">{o.body}</p>
                </div>
            ))}
        </div>
    );
}

function SignalsList({ items }) {
    if (items.length === 0) return <Empty>{i18n.t("cdrawer.no_signals")}</Empty>;
    return (
        <div className="space-y-2">
            {items.map((s) => (
                <div key={s.id} className="rounded-lg border border-white/10 bg-[var(--jr-surface-2)] p-3" data-testid={`cd-signal-${s.id}`}>
                    <div className="flex items-center justify-between gap-2">
                        <div className="min-w-0 flex-1">
                            <div className="text-sm text-white truncate">{s.signal_title || "—"}</div>
                            <div className="text-[10px] font-mono text-[var(--jr-text-3)] mt-0.5">
                                {s.signal_type} · score {s.score} · {(s.created_at || "").slice(0, 10)}
                            </div>
                        </div>
                        <span className="text-[9px] font-mono uppercase tracking-[0.14em] px-1.5 py-0.5 rounded bg-white/5 text-[var(--jr-text-2)] shrink-0">{s.status}</span>
                    </div>
                </div>
            ))}
            <Link to="/app/sales/signals" data-testid="cd-view-signals"
                className="inline-flex items-center gap-1.5 text-xs text-[var(--jr-info)] hover:text-white">
                {i18n.t("cdrawer.view_signals")} <ArrowRight size={11} />
            </Link>
        </div>
    );
}

function Badge({ cls, children }) {
    return <span className={`text-[9px] font-mono uppercase tracking-[0.14em] px-1.5 py-0.5 rounded ${cls}`}>{children}</span>;
}
function Kv({ label, value }) {
    return (
        <div>
            <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-[var(--jr-text-3)]">{label}</div>
            <div className="text-sm text-white mt-0.5 break-words">{value}</div>
        </div>
    );
}
function Empty({ children }) {
    return <div className="text-center py-8 text-xs text-[var(--jr-text-3)]">{children}</div>;
}
